import { useState } from "react";
import { Upload, ArrowLeft, ArrowRight, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { imgUrl } from "../lib/shop";

export async function uploadFiles(files) {
  const out = [];
  for (const file of files) {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/upload", fd);
      out.push(r.data);
    } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
  }
  return out;
}

export function UploadButton({ onUploaded, multiple = true, label = "Upload photos", testid = "image-upload-input" }) {
  const [busy, setBusy] = useState(false);
  const change = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    onUploaded(await uploadFiles(files));
    setBusy(false);
  };
  return (
    <label className="btn-outline cursor-pointer !min-h-[44px]">
      <Upload size={16} /> {busy ? "Uploading..." : label}
      <input type="file" accept="image/jpeg,image/png,image/webp" multiple={multiple} className="hidden" onChange={change} disabled={busy} data-testid={testid} />
    </label>
  );
}

export function ImageManager({ images, onChange, colours = [] }) {
  const move = (i, d) => {
    const next = [...images];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  const cover = (i) => onChange([images[i], ...images.filter((_, j) => j !== i)]);
  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="product-image-list">
        {images.map((im, i) => (
          <div key={im.id || im.url} className={`relative rounded-xl overflow-hidden border-2 ${i === 0 ? "border-[#d4af37]" : "border-white/10"}`} data-testid={`product-image-${i}`}>
            <img src={imgUrl(im.url)} alt="" className="aspect-square w-full object-cover" />
            {i === 0 && <span className="absolute top-1 left-1 text-[9px] font-extrabold bg-[#d4af37] text-black px-1.5 py-0.5 rounded">COVER</span>}
            {colours.length > 0 && (
              <select value={im.colour || ""} onChange={(e) => onChange(images.map((x, j) => (j === i ? { ...x, colour: e.target.value || null } : x)))}
                className="absolute top-1 right-1 max-w-[70%] text-[10px] font-bold bg-black/80 border border-white/20 rounded px-1 py-0.5" aria-label="Photo colour" data-testid={`image-colour-${i}`}>
                <option value="">No colour</option>{colours.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            )}
            <div className="absolute bottom-0 inset-x-0 bg-black/75 flex justify-around py-1">
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move left" className="p-1 disabled:opacity-30"><ArrowLeft size={14} /></button>
              <button type="button" onClick={() => cover(i)} aria-label="Set cover" className="p-1" data-testid={`image-cover-${i}`}><Star size={14} /></button>
              <button type="button" disabled={i === images.length - 1} onClick={() => move(i, 1)} aria-label="Move right" className="p-1 disabled:opacity-30"><ArrowRight size={14} /></button>
              <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))} aria-label="Delete" className="p-1 text-red-400" data-testid={`image-delete-${i}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3"><UploadButton onUploaded={(added) => onChange([...images, ...added])} /></div>
      <p className="text-xs text-zinc-500 mt-2">Photos from your phone or computer are automatically resized and optimised. The first photo is the cover image.</p>
    </div>
  );
}
